import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { EASE_SNAP, DURATION, usePrefersReducedMotion } from '../ui/motion';

export interface TabDef {
  key: string;
  label: string;
}

interface TabBarProps {
  tabs: TabDef[];
  active: string;
  onChange: (key: string) => void;
  id?: string;
}

/**
 * Tab bar with ONE sliding indicator instead of N independently recolouring
 * buttons — the JitsLab `BottomNav` technique, ported.
 *
 * Indicator position comes from the tab's measured DOM rect, not percentage
 * math, so it stays correct when labels differ in width.
 */
export function TabBar({ tabs, active, onChange, id }: TabBarProps) {
  const containerRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [rect, setRect] = useState({ left: 0, width: 0, ready: false });
  const reduced = usePrefersReducedMotion();
  const duration = reduced ? 0 : DURATION.slow;

  const measure = () => {
    const container = containerRef.current;
    const idx = tabs.findIndex(t => t.key === active);
    const el = idx >= 0 ? tabRefs.current[idx] : null;
    if (!container || !el) {
      setRect(r => ({ ...r, ready: false }));
      return;
    }
    const elRect = el.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();
    setRect({ left: elRect.left - cRect.left, width: elRect.width, ready: true });
  };

  useLayoutEffect(measure, [active, tabs]);

  useEffect(() => {
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, tabs]);

  return (
    <nav
      id={id}
      ref={containerRef}
      className="pear-tabs"
      aria-label="Sections"
    >
      <span
        className="pear-tab-indicator"
        aria-hidden="true"
        style={{
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          opacity: rect.ready ? 1 : 0,
          transition: reduced
            ? 'none'
            : `left ${duration}ms ${EASE_SNAP}, width ${duration}ms ${EASE_SNAP}, opacity 160ms linear`,
        }}
      />
      {tabs.map(tab => (
        <button
          key={tab.key}
          ref={el => {
            tabRefs.current[tabs.indexOf(tab)] = el;
          }}
          type="button"
          role="tab"
          id={`tab-${tab.key}`}
          aria-selected={tab.key === active}
          className={`pear-btn pear-tab${tab.key === active ? ' is-active' : ''}`}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </nav>
  );
}
